import { useState } from "react";
import { Star, Play, X } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";

const TestimonialsSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const testimonials = [
    {
      name: "Siddharth Saxena",
      text: "The service I received was truly great. Dr. Seema is very knowledgeable and caring. Highly recommend for anyone seeking physiotherapy.",
      rating: 5,
    },
    {
      name: "Neha Srivastava",
      text: "The staff is highly professional, knowledgeable, and compassionate. My diastasis recti has improved significantly after just 2 months of treatment.",
      rating: 5,
    },
    {
      name: "Rashi Raman",
      text: "I highly recommend her work. After pregnancy, I was struggling with back pain and weak core. Now I feel so much stronger and confident.",
      rating: 5,
    },
  ];

  const videoTestimonials = [
    { id: "0dPf6OEYYLo", title: "Patient Recovery Story 1" },
    { id: "51jpxG9u5S0", title: "Patient Recovery Story 2" },
    { id: "5OdfVGM3iVg", title: "Patient Recovery Story 3" },
    { id: "7WMMe8DEoBM", title: "Patient Recovery Story 4" },
    { id: "NLZXceuimrQ", title: "Patient Recovery Story 5" },
    { id: "Z9K40vSavQY", title: "Patient Recovery Story 6" },
    { id: "gOTuLDZWWXI", title: "Patient Recovery Story 7" },
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-pink-50 to-white" data-testid="testimonials-section">
      <div className="section-container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-white text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4 shadow-sm">
            Patient Stories
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-testid="testimonials-headline">
            What Our Patients Say
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-yellow-400" fill="currentColor" size={24} />
              ))}
            </div>
            <span className="text-stone-600 font-medium">5.0 from 50+ Google Reviews</span>
          </div>
        </div>

        {/* Text Testimonials */}
        <div className="grid md:grid-cols-3 gap-6 mb-20" data-testid="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="card-testimonial quote-decoration"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="relative z-10">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400" fill="currentColor" size={18} />
                  ))}
                </div>
                <p className="text-stone-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900">{testimonial.name}</p>
                    <p className="text-xs text-stone-500">Verified Patient</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Testimonials Section */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-blue-900 mb-2">Video Success Stories</h3>
            <p className="text-stone-600">Watch our patients share their recovery journey</p>
          </div>

          <div className="relative px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent>
                {videoTestimonials.map((video) => (
                  <CarouselItem key={video.id} className="md:basis-1/2 lg:basis-1/3 p-2">
                    <div 
                      className="group relative aspect-video rounded-2xl overflow-hidden bg-stone-200 cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                      onClick={() => setSelectedVideo(video.id)}
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`} 
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center text-blue-700 group-hover:bg-blue-600 group-hover:text-white transition-all transform group-hover:scale-110">
                          <Play fill="currentColor" size={24} />
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-4" />
              <CarouselNext className="hidden md:flex -right-4" />
            </Carousel>
          </div>
        </div>

        {/* Video Lightbox Modal */}
        <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <DialogContent className="max-w-4xl p-0 bg-black border-none overflow-hidden aspect-video sm:rounded-2xl shadow-2xl">
            {selectedVideo && (
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            )}
          </DialogContent>
        </Dialog>

        {/* Google Review CTA */}
        <div className="text-center mt-16">
          <a
            href="https://www.google.com/maps/place/Sky's+PhysioRehab+%26+Slimming+Centre"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium transition-colors"
            data-testid="google-reviews-link"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Read All Reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
